<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import facebookClient from 'dashboard/api/facebook';

import PageList from './facebook/PageList.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const isLoading = ref(true);
const isAuthorizing = ref(false);
const busyPageId = ref('');
const pages = ref([]);
const source = ref('account');
const errorMessage = ref('');
const disconnectDialogRef = ref(null);
const pageToDisconnect = ref(null);

// Chave opaca devolvida pelo callback do OAuth. Presente apenas logo após o
// usuário voltar da Meta; expira em 10 minutos no servidor.
const fbSession = computed(() => route.query.fb_session || '');

const hasConnectedPages = computed(() => pages.value.some(p => p.connected));

// Tira o fb_session da URL para que um F5 não tente reusar uma chave já gasta,
// e para que a chave não fique no histórico do navegador.
const clearSessionFromUrl = () => {
  router.replace({ query: {} });
};

const loadPages = async () => {
  isLoading.value = true;
  try {
    const { data } = await facebookClient.listPages(fbSession.value);
    pages.value = data.pages || [];
    source.value = data.source || 'account';
  } catch (error) {
    // Sessão expirada não é falha: o usuário só demorou. Cai para a lista do banco.
    if (error.response?.status === 401 && fbSession.value) {
      clearSessionFromUrl();
      await loadPages();
      return;
    }
    errorMessage.value =
      error.response?.data?.message ||
      t('INTEGRATION_SETTINGS.FACEBOOK.ERROR.LOAD_PAGES');
  } finally {
    isLoading.value = false;
  }
};

const authorize = async () => {
  isAuthorizing.value = true;
  try {
    const { data } = await facebookClient.generateAuthorization();
    window.location.href = data.url;
  } catch (error) {
    isAuthorizing.value = false;
    errorMessage.value =
      error.response?.data?.error === 'app_not_configured'
        ? t('INTEGRATION_SETTINGS.FACEBOOK.ERROR.APP_NOT_CONFIGURED')
        : t('INTEGRATION_SETTINGS.FACEBOOK.ERROR.AUTHORIZE');
  }
};

const connectPage = async page => {
  busyPageId.value = page.id;
  try {
    await facebookClient.connectPage({
      fbSession: fbSession.value,
      pageId: page.id,
      inboxName: page.name,
    });
    useAlert(t('INTEGRATION_SETTINGS.FACEBOOK.CONNECT.SUCCESS'));
    await loadPages();
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        t('INTEGRATION_SETTINGS.FACEBOOK.CONNECT.ERROR')
    );
  } finally {
    busyPageId.value = '';
  }
};

const openDisconnectDialog = page => {
  pageToDisconnect.value = page;
  disconnectDialogRef.value?.open();
};

const confirmDisconnect = async () => {
  const page = pageToDisconnect.value;
  if (!page) return;

  busyPageId.value = page.id;
  try {
    await facebookClient.disconnectPage(page.id);
    useAlert(t('INTEGRATION_SETTINGS.FACEBOOK.DISCONNECT.SUCCESS'));
    await loadPages();
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.FACEBOOK.DISCONNECT.ERROR'));
  } finally {
    busyPageId.value = '';
    pageToDisconnect.value = null;
  }
};

onMounted(() => {
  // O callback redireciona para cá com error_type quando o usuário cancela na Meta.
  if (route.query.error_type) {
    errorMessage.value =
      route.query.error_type === 'access_denied'
        ? t('INTEGRATION_SETTINGS.FACEBOOK.ERROR.ACCESS_DENIED')
        : route.query.error_message ||
          t('INTEGRATION_SETTINGS.FACEBOOK.ERROR.AUTHORIZE');
  }
  loadPages();
});
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.FACEBOOK.HEADER')"
        :description="$t('INTEGRATION_SETTINGS.FACEBOOK.DESCRIPTION')"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6">
        <div
          v-if="errorMessage"
          class="p-4 rounded-xl bg-n-ruby-2 dark:bg-n-ruby-3 text-n-ruby-11 text-body-main"
        >
          {{ errorMessage }}
        </div>

        <div
          class="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-6 outline outline-n-container outline-1 bg-n-card rounded-xl"
        >
          <div>
            <h3 class="mb-1 text-heading-1 text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.FACEBOOK.CONNECT.TITLE') }}
            </h3>
            <p class="text-n-slate-11 text-body-main">
              {{ $t('INTEGRATION_SETTINGS.FACEBOOK.CONNECT.SUBTITLE') }}
            </p>
          </div>
          <Button
            faded
            blue
            :is-loading="isAuthorizing"
            :label="
              hasConnectedPages
                ? $t('INTEGRATION_SETTINGS.FACEBOOK.CONNECT.ANOTHER')
                : $t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')
            "
            @click="authorize"
          />
        </div>

        <div v-if="pages.length" class="flex flex-col gap-3">
          <h4 class="text-heading-3 text-n-slate-12">
            {{
              source === 'facebook'
                ? $t('INTEGRATION_SETTINGS.FACEBOOK.PAGE.AVAILABLE_TITLE')
                : $t('INTEGRATION_SETTINGS.FACEBOOK.PAGE.CONNECTED_TITLE')
            }}
          </h4>
          <PageList
            :pages="pages"
            :source="source"
            :busy-page-id="busyPageId"
            @connect="connectPage"
            @disconnect="openDisconnectDialog"
          />
        </div>

        <p
          v-else-if="!isLoading"
          class="text-n-slate-11 text-body-main text-center py-10"
        >
          {{ $t('INTEGRATION_SETTINGS.FACEBOOK.PAGE.EMPTY') }}
        </p>
      </div>

      <Dialog
        ref="disconnectDialogRef"
        type="alert"
        :title="$t('INTEGRATION_SETTINGS.FACEBOOK.DISCONNECT.CONFIRM_TITLE')"
        :description="
          $t('INTEGRATION_SETTINGS.FACEBOOK.DISCONNECT.CONFIRM_MESSAGE')
        "
        :confirm-button-label="
          $t('INTEGRATION_SETTINGS.FACEBOOK.PAGE.DISCONNECT')
        "
        :cancel-button-label="$t('INTEGRATION_SETTINGS.FACEBOOK.CANCEL')"
        @confirm="confirmDisconnect"
      />
    </template>
  </SettingsLayout>
</template>
